/**
 * API Error Handler
 * Centralized error handling and user-friendly error messages
 */

import { ApiError } from "../../types/api";

export class ErrorHandler {
  /**
   * Convert HTTP response to ApiError
   */
  static async handleResponse(response: Response): Promise<any> {
    if (response.ok) {
      try {
        return await response.json();
      } catch {
        return null;
      }
    }

    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText };
    }

    throw new ApiError(
      response.status,
      errorData.code || `HTTP_${response.status}`,
      errorData.message || this.getDefaultErrorMessage(response.status),
      errorData
    );
  }

  /**
   * Get user-friendly error message based on status code
   */
  private static getDefaultErrorMessage(statusCode: number): string {
    switch (statusCode) {
      case 400:
        return "Invalid request. Please check your input.";
      case 401:
        return "You are not authenticated. Please log in again.";
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 409:
        return "A conflict occurred. The resource may already exist.";
      case 429:
        return "Too many requests. Please try again later.";
      case 500:
        return "Server error. Please try again later.";
      case 503:
        return "Service temporarily unavailable. Please try again later.";
      default:
        return "An unexpected error occurred.";
    }
  }

  /**
   * Check if error is recoverable (should retry)
   */
  static isRecoverable(error: ApiError): boolean {
    return [408, 429, 500, 502, 503, 504].includes(error.statusCode);
  }

  /**
   * Check if error is authentication related
   */
  static isAuthError(error: ApiError): boolean {
    return error.statusCode === 401;
  }

  /**
   * Log error for monitoring
   */
  static logError(error: ApiError | Error, context?: string): void {
    if (error instanceof ApiError) {
      console.error(`[API Error] ${context || ""}`, {
        statusCode: error.statusCode,
        code: error.code,
        message: error.message,
        details: error.details,
      });
    } else {
      console.error(`[Error] ${context || ""}`, error);
    }
  }
}
